import React, { useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { formatDate } from "../utils/helpers";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "../api/api";

export default function History() {
  const { user } = useApp();
  const [orderHistory, setOrderHistory] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState({});
  const [feedback, setFeedback] = useState({});
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const navigate = useNavigate();

  const toggleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  // 🔹 Fetch order history from API
  useEffect(() => {
    if (!user?.Vid) return;
    setLoading(true);

    const userType = user.UserType || "Existing User";

    api
      .get("OrderHistory", { Vid: user.Vid, UserType: userType })
      .then((res) => {
        const data = res?.OrderHistoryjs?.Table || [];
        const grouped = Object.values(
          data.reduce((acc, row) => {
            if (!acc[row.OId]) {
              acc[row.OId] = {
                OId: row.OId,
                Vid: row.Vid,
                Vname: row.Vname,
                Address1: row.Address1,
                ShortName: row.ShortName,
                mobile: row.mobile,
                Area: row.Area,
                Division: row.Division,
                OrderDate: row.OrderDate,
                DeliveryDate: row.DeliveryDate,
                Paymode: row.Paymode,
                DaysNeeded: row.DaysNeeded,
                IsConfirmed: row.IsConfirmed,
                items: [],
                Ratings: row.Ratings || 0,
                Feedback: row.Feedback || '',
                DeliveryNote: row.DeliveryNote || '',
                PaymentRemark: row.PaymentRemark || '',
                IsTakeAway: row.IsTakeAway, 
              };
            }
            acc[row.OId].items.push({
              itemid: row.itemid,
              item_desc: row.item_desc,
              qty: row.qty,
              Standardcost: row.Standardcost,
              Amount: row.Amount,
              FoodType: row.FoodType, // Include FoodType with each item
               DeliveryDate: row.DeliveryDate  
            });
            return acc;
          }, {})
        );

        // Sort by OId descending (newest first)
        const sortedGrouped = grouped.sort((a, b) => b.OId - a.OId);

        setOrderHistory(sortedGrouped);
      })
      .catch((err) => console.error("Failed to load history:", err))
      .finally(() => setLoading(false));
  }, [user?.Vid, user?.UserType]);

  // 🔹 Handle rating click
 const handleRating = async (orderId, value) => {
  setRatings(prev => ({ ...prev, [orderId]: value }));

  if (!user?.Vid) return;

  try {
    const rawFeedback = feedback[orderId];
    const finalFeedback =
      typeof rawFeedback === "string" && rawFeedback.trim()
        ? rawFeedback.trim()
        : "N/A";

    await api.OrderRatingsFeedback(
      user.Vid,
      orderId,
      value,
      finalFeedback
    );
  } catch (err) {
    console.error("Failed to update rating:", err);
  }
};


  // 🔹 Handle feedback submit
 const handleFeedbackSubmit = async (orderId) => {
  if (!user?.Vid) return;

  const rating = Number(ratings[orderId] || orderHistory.find(o => o.OId === orderId)?.Ratings || 0);

  const rawFeedback = feedback[orderId];
  const finalFeedback =
    typeof rawFeedback === "string" && rawFeedback.trim().length > 0
      ? rawFeedback.trim()
      : "N/A"; // ✅ never empty

  try {
    await api.OrderRatingsFeedback(
      user.Vid,
      orderId,
      rating,
      finalFeedback
    );

    setFeedbackMessage("Feedback submitted successfully!");
    setTimeout(() => setFeedbackMessage(""), 3000);
  } catch (err) {
    console.error("Failed to submit feedback:", err);
    setFeedbackMessage("Failed to submit feedback. Please try again.");
    setTimeout(() => setFeedbackMessage(""), 3000);
  }
};

  if (loading)
    return (
      <p className="mt-10 text-center text-gray-600 animate-pulse">
        Loading your order history...
      </p>
    );

  if (!orderHistory || orderHistory.length === 0)
    return (
<div className="max-w-md p-6 mx-auto mt-10 text-center border border-gray-200 rounded-lg bg-gray-50">
  <div className="flex justify-center mb-4">
    <svg
      className="w-10 h-10 text-indigo-500"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 8V6a4 4 0 00-8 0v2m-3 0h14a1 1 0 011 1v10a1 1 0 01-1 1H5a1 1 0 01-1-1V9a1 1 0 011-1z"
      />
    </svg>
  </div>

  <h3 className="mb-2 text-sm font-semibold text-gray-800">
    No Orders Yet
  </h3>

  <p className="text-sm text-gray-600">
    If you've just placed your first order, please log out and log back in to refresh your order history.
  </p>
</div>

    );

  // 🔹 Food type color mapping (with richer style)
  const foodTypeStyles = {
    lunch:
      "bg-gradient-to-r from-red-400 to-red-500 text-white text-lg font-bold px-5 py-1.5 rounded-full shadow-md tracking-wide",
    dinner:
      "bg-gradient-to-r from-blue-500 to-blue-600 text-white text-lg font-bold px-5 py-1.5 rounded-full shadow-md tracking-wide",
    breakfast:
      "bg-gradient-to-r from-green-500 to-green-600 text-white text-lg font-bold px-5 py-1.5 rounded-full shadow-md tracking-wide",
    other:
      "bg-gradient-to-r from-purple-500 to-purple-600 text-white text-lg font-bold px-5 py-1.5 rounded-full shadow-md tracking-wide",
  };

  return (
    <div className="pb-10 space-y-6 animate-fade-in">
      {/* 🔙 Back Button */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 font-medium text-gray-700 transition bg-gray-100 shadow-sm hover:bg-gray-200 rounded-xl"
        >
          ← Back
        </button>
        <h2 className="flex-1 text-2xl font-bold text-center text-gray-800">
          Order History
        </h2>
      </div>

      {feedbackMessage && (
        <div className={`fixed top-20 left-1/2 transform -translate-x-1/2 text-white px-8 py-4 rounded-xl shadow-2xl z-50 animate-fade-in border ${feedbackMessage.includes('successfully') ? 'bg-gradient-to-r from-green-500 to-green-600 border-green-400' : 'bg-gradient-to-r from-red-500 to-red-600 border-red-400'}`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">{feedbackMessage.includes('successfully') ? '✅' : '❌'}</span>
            <span className="text-lg font-semibold">{feedbackMessage}</span>
          </div>
        </div>
      )}
      
      {orderHistory.map((order) => {
        const total = order.items.reduce(
          (sum, i) => sum + Number(i.Amount || 0),
          0
        );
        const status =
          order.IsConfirmed === 1 ? "Confirmed" : "Pending Confirmation";

        const statusColor =
          order.IsConfirmed === 1
            ? "bg-green-100 text-green-700"
            : "bg-red-100 text-red-700";

        const foodKey = order.FoodType?.toLowerCase() || "";
        const foodClass = foodTypeStyles[foodKey] || "bg-gray-300 text-gray-800";

        const orderType = order.IsTakeAway === 1 ? "🛍️ TAKEAWAY" : "🛵 DELIVERY";
        const orderTypeStyle =
        order.IsTakeAway === 1
          ? "bg-teal-100 text-teal-700"
          : "bg-yellow-100 text-yellow-700";
        const itemCount = order.items.length;
        const totalQty = order.items.reduce((sum, i) => sum + Number(i.qty || 0), 0);
        const deliveryShortName = order.IsTakeAway === 1 ? "" : String(order.ShortName || "").trim();

        return (
          <motion.div
            key={order.OId}
            layout
            className="p-5 transition bg-white shadow-md cursor-pointer rounded-2xl hover:shadow-lg"
            onClick={() => toggleExpand(order.OId)}
          >
            {/* Header */}
           <div className="flex items-start justify-between w-full">
  <h3 className="text-lg font-semibold">Order #{order.OId}</h3>

  <span
    className={`text-xs px-3 py-1 rounded-full font-medium ${statusColor}`}
  >
    {status} 
  </span>
</div>

{/* Order Info */}
<div className="mt-2 space-y-1 text-sm text-gray-600">
  <p>Ordered on: {formatDate(order.OrderDate)}</p>

  <p>
    Paymode: <span className="font-medium text-gray-700">{order.Paymode}</span>
  </p>

  <p>
    Items: {itemCount} &nbsp; Qty: {totalQty}
  </p>
</div>

{deliveryShortName && (
  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 mt-3 text-xs font-bold text-red-700 bg-red-50 border border-red-100 rounded-full">
    <span>Delivery Address</span>
    <span className="text-gray-900">{deliveryShortName}</span>
  </div>
)}

{/* Total + Delivery Type */}
<div className="flex items-end justify-between mt-4">
  <div>
    <p className="text-xs text-gray-500">Total</p>
    <p className="text-xl font-bold">₹ {total.toFixed(2)}</p>
  </div>

  <span
    className={`text-xs px-3 py-1 rounded-full font-medium ${orderTypeStyle}`}
  >
    {orderType}
  </span>
</div>

     
      


            {/* Expandable Details */}
            <AnimatePresence>
              {expanded === order.OId && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="pt-4 mt-4 space-y-4 border-t"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="pt-3 border-t">
                    <h4 className="mb-2 font-semibold text-gray-800">
                      Items Ordered
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm text-gray-600 border">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 text-left">Item</th>
                            <th className="p-2 text-center">Food Type</th>
                            <th className="p-2 text-center">Delivery Date</th>
                            <th className="p-2 text-center">Qty</th>
                            <th className="p-2 text-center">Rate</th>
                            <th className="p-2 text-center">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items.map((i, idx) => (
                            <tr
                              key={idx}
                              className="border-t hover:bg-red-50"
                            >
                              <td className="p-2">{i.item_desc}</td>
                              <td className="p-2 text-center">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  i.FoodType === 'lunch' ? 'bg-red-100 text-red-700' :
                                  i.FoodType === 'dinner' ? 'bg-blue-100 text-blue-700' :
                                  i.FoodType === 'breakfast' ? 'bg-green-100 text-green-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {i.FoodType}
                                </span>
                              </td>
                               <td className="p-2 text-sm text-center text-gray-600">
    {formatDate(i.DeliveryDate)}
  </td>
                              <td className="p-2 text-center">{i.qty}</td>
                              <td className="p-2 text-center">
                                ₹{i.Standardcost.toFixed(2)}
                              </td>
                              <td className="p-2 text-center">
                                ₹{Number(i.Amount).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="font-semibold text-right text-gray-800">
                      Total: ₹{total.toFixed(2)}
                    </div>
                    
                    {/* 📝 Delivery & Payment Remarks */}
                    {(order.DeliveryNote || order.PaymentRemark) && (
                      <div className="p-3 mt-3 rounded-lg bg-gray-50">
                        {order.DeliveryNote && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Delivery Note:</span> {order.DeliveryNote}
                          </p>
                        )}
                        {order.PaymentRemark && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Payment Remark:</span> {order.PaymentRemark}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* ⭐ Interactive Ratings (only enabled if IsConfirmed === 1) */}
                    {order.IsConfirmed === 1 && (
                      <>
                        <div className="flex items-center gap-1 mt-2">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <span
                              key={star}
                              onClick={() => handleRating(order.OId, star)}
                              className={`text-2xl cursor-pointer transition ${star <= (ratings[order.OId] || order.Ratings || 0)
                                ? "text-red-400 scale-110"
                                : "text-gray-300 hover:text-red-400"
                                }`}
                            >
                              ★
                            </span>
                          ))}
                        </div>
                        
                        {/* 📝 Feedback Input */}
                        <div className="mt-2">
                          <textarea
                            value={feedback[order.OId] !== undefined ? feedback[order.OId] : (order.Feedback || "")}
                            onChange={(e) => setFeedback({ ...feedback, [order.OId]: e.target.value })}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Share your feedback..."
                            className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-red-300 focus:border-red-400"
                            rows="2"
                          />
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFeedbackSubmit(order.OId);
                            }}
                            className="mt-2 px-4 py-1.5 bg-red-500 text-white rounded-lg text-sm font-semibold hover:bg-red-600 transition-colors"
                          >
                            Submit Feedback
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}
